import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Stack, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { BaseDialog } from '@/components/shared/base-dialog';
import { useNotification } from '@/context/notification';
import {
  useCreateUserFeedback,
  useDeleteUserFeedback,
} from '@/data/guidance/feedback';
import { ConfirmDialog } from '@/packages/skill-management/components/feedbacks/confirm-dialog';
import { DeleteConfirmDialog } from '@/packages/skill-management/components/feedbacks/delete-confirm-dialog';
import { useSkills } from '@/packages/skill-management/context/skills';

interface EditFeedbackDialogProps {
  onClose: () => void;
  onSuccess: (feedbackId: number) => void;
}

const MAX_FEEDBACK_LENGTH = 10000;

const feedbackValidationSchema = (previousFeedback?: string) =>
  z.object({
    userFeedback: z
      .string()
      .min(1, 'Preencha com um feedback válido')
      .max(MAX_FEEDBACK_LENGTH, `Máximo de ${MAX_FEEDBACK_LENGTH} caracteres`)
      .refine(
        (value) => !previousFeedback || value !== previousFeedback,
        'O feedback precisa ser diferente do anterior'
      ),
  });

type FeedbackFormData = z.infer<ReturnType<typeof feedbackValidationSchema>>;

export const EditFeedbackDialog = ({
  onClose,
  onSuccess,
}: EditFeedbackDialogProps) => {
  const { userFeedback } = useSkills();
  const { mutate: create, isPending: isCreating } = useCreateUserFeedback();
  const { mutate: deleteFeedback } = useDeleteUserFeedback();
  const { toast } = useNotification();
  const [isEditing, setIsEditing] = useState(!userFeedback?.content);
  const [newConfirmDialogOpen, setNewConfirmDialogOpen] = useState(false);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);

  const { control, handleSubmit, watch, reset } = useForm<FeedbackFormData>({
    defaultValues: { userFeedback: userFeedback?.content || '' },
    resolver: zodResolver(feedbackValidationSchema(userFeedback?.content)),
  });

  const currentFeedback = watch('userFeedback');

  const onSubmit = (data: FeedbackFormData) => {
    create(data.userFeedback, {
      onSuccess: (response) => {
        onSuccess(response.id);
      },
      onError: () => {
        toast({ content: 'Erro ao atualizar feedback', type: 'error' });
      },
    });
  };

  const handleDelete = () => {
    deleteFeedback(undefined, {
      onSuccess: () => {
        toast({ content: 'Feedback removido com sucesso', type: 'success' });
        onClose();
        setDeleteConfirmDialogOpen(false);
      },
      onError: () => {
        toast({ content: 'Erro ao remover feedback', type: 'error' });
        setDeleteConfirmDialogOpen(false);
      },
    });
  };

  const handleNewFeedback = () => {
    setIsEditing(true);
    reset({ userFeedback: '' });
  };

  const viewActions = (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      gap={2}
      sx={{ width: '100%' }}
    >
      <Button onClick={() => setNewConfirmDialogOpen(true)} sx={{ flex: 1 }}>
        Substituir
      </Button>
      <Button
        variant="text"
        color="error"
        onClick={() => setDeleteConfirmDialogOpen(true)}
        sx={{ flex: 1 }}
      >
        Remover
      </Button>
    </Stack>
  );

  const editingActions = (
    <>
      <Button variant="text" onClick={onClose}>
        Cancelar
      </Button>
      <Button
        variant="contained"
        type="submit"
        disabled={isCreating}
        loading={isCreating}
        onClick={(e) => {
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
      >
        Avançar
      </Button>
    </>
  );

  const dialogActions = isEditing ? editingActions : viewActions;

  return (
    <BaseDialog
      onClose={onClose}
      open
      title="Feedback"
      fullWidth
      actionsComponent={dialogActions}
    >
      <Stack gap={2}>
        <Typography>
          Coloque seu último feedback no campo de texto abaixo para
          identificarmos as habilidades importantes para o seu
          desenvolvimento. Elas serão usadas para guiar sua experiência na
          plataforma.
        </Typography>
        <Controller
          control={control}
          name="userFeedback"
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              disabled={!isEditing}
              error={!!error}
              fullWidth
              helperText={
                error?.message ||
                `${currentFeedback?.length || 0}/${MAX_FEEDBACK_LENGTH} ${currentFeedback?.length === MAX_FEEDBACK_LENGTH ? 'Você atingiu o limite de caracteres, verifique se a mensagem está completa e garanta melhores habilidades.' : ''}`
              }
              multiline
              placeholder="Ex.: O próximo ciclo você precisará desenvolver sua comunicação, tempo de reatividade e visibilidade dos assuntos."
              rows={10}
              variant="outlined"
              slotProps={{ htmlInput: { maxLength: MAX_FEEDBACK_LENGTH } }}
            />
          )}
        />
      </Stack>
      {newConfirmDialogOpen && (
        <ConfirmDialog
          onClose={() => setNewConfirmDialogOpen(false)}
          onSubmit={() => {
            handleNewFeedback();
            setNewConfirmDialogOpen(false);
          }}
        />
      )}
      {deleteConfirmDialogOpen && (
        <DeleteConfirmDialog
          onClose={() => setDeleteConfirmDialogOpen(false)}
          onSubmit={handleDelete}
        />
      )}
    </BaseDialog>
  );
};
